import { api } from '@/lib/axios-client'

export type CornerCoord = { lat: number; lon: number }

export type GeoTransformResponse = {
  corners: {
    topLeft: CornerCoord
    topRight: CornerCoord
    bottomRight: CornerCoord
    bottomLeft: CornerCoord
  }
  // Geo-position of drawingBounds' own rectangle corners — NOT the same as
  // `corners` above, which bounds the matched parcel/union polygon and can
  // legitimately be a smaller/offset region within the drawing. Full-page
  // rendering must extrapolate from this pair (see computePageCorners in
  // renderPdfPage.ts), not from `corners`.
  drawingBoundsCorners: {
    topLeft: CornerCoord
    topRight: CornerCoord
    bottomRight: CornerCoord
    bottomLeft: CornerCoord
  }
  center: CornerCoord
  rotationDegrees: number
  scaleFeetPerInch: number
  drawingBounds: {
    minX: number
    maxX: number
    minY: number
    maxY: number
    pageHeightUnits: number
  }
  parcelCoordinates: GeoJSON.Feature | null
  parcelLineOpacity: number
  parcelFillOpacity: number
  /**
   * The drawn property line, projected to lat/lon — the boundary as it appears
   * on the sheet, not the assessor's parcel.
   *
   * This is the boundary to draw. Unlike parcelCoordinates it survives the two
   * approximate paths (`address-point`, `mismatched-parcel-centroid`), which
   * deliberately send no parcel rather than outline one the backend couldn't
   * verify — that is why those sheets used to show no boundary at all.
   *
   * Optional: the backend only began sending it in Aug 2026, and a frontend
   * deploy can lead the backend one. Null when the extraction found no usable
   * polygon.
   */
  propertyLineCoordinates?: CornerCoord[] | null
  // True when the position OR the rotation is coarser than a parcel matched
  // and calibrated by surveyed bearings. It does NOT mean "anchored to an
  // address point" on its own — read positionSource/rotationSource for which
  // of the several causes applied (see approximateReason in MapOverlayPanel).
  isApproximatePosition: boolean
  // All three are optional: the backend only began sending them in Aug 2026,
  // and a frontend deploy can lead the backend one.
  //
  // positionSource — which anchoring strategy placed the overlay:
  //   mapoverlay-api-corner-fit  scale/rotation/insertion solved by
  //                              mapoverlay-api from the drawn border corners
  //                              (the AutoCAD.MapOverlay alignment)
  //   parcel-fit                 same, solved locally from the full drawn
  //                              property-line ring
  //   parcel-centroid            drawing centroid on the parcel centroid,
  //                              sized by the scale read off the sheet
  //   parcel-union-centroid      as above, parcel is a union of several lots
  //   mismatched-parcel-centroid matched parcel failed its area check
  //   address-point              no usable parcel — centroid pinned to the
  //                              plain address geocode (a rooftop, for a
  //                              multi-tenant site)
  positionSource?: string
  // rotationSource — which measurement set the orientation, e.g.
  //   `mapoverlay-api-corner-fit/north-arrow` (the drawn arrow, cross-checked
  //   against the corners), `.../corner-fit`, `bearing`,
  //   `bearing-cross-checked`, `bearing-north-arrow-disagreement`,
  //   `north-arrow`.
  rotationSource?: string
  // Diagnostics from fitting the drawn property-line ring onto the parcel
  // ring. Present on the local paths; null when there was nothing to fit.
  // residualFt is the one to read — judge it against the site's own size.
  parcelFit?: {
    rotationDegrees: number
    scaleFeetPerInch: number
    scaleXFeetPerInch: number
    scaleYFeetPerInch: number
    residualFt: number
    affineResidualFt: number
    shearDegrees: number
    isMirrored: boolean
    sampleCount: number
  } | null
}

export type GeoTransformStatus = 'Pending' | 'Computing' | 'Ready' | 'Failed'

// What GET /MapOverLay/{id}/geo-transform now returns. The computation runs on
// the `geo-transform` queue rather than inside the request, so the endpoint
// answers with a status and only carries `transform` once it is Ready. 200 for
// Ready, 202 otherwise — but the client reads `status`, not the HTTP code,
// because the axios interceptor unwraps the envelope and discards the response.
export type GeoTransformStatusResponse = {
  status: GeoTransformStatus
  transform: GeoTransformResponse | null
  error?: string | null
  requestedAt?: string | null
}

/**
 * The axios client in this project unwraps the { data, isSuccess, errors }
 * envelope automatically — api.get<T> returns response.data.data directly.
 * When isSuccess is false the interceptor throws with the backend error message.
 * When the response is null it means a network error or non-JSON body.
 *
 * Passing refresh forces a recomputation of a sheet whose cached transform is
 * visibly wrong, and is also how a Failed transform is retried.
 */
export const getMapOverlayGeoTransform = async (
  attachmentId: string,
  refresh = false,
): Promise<GeoTransformStatusResponse> => {
  const data = await api.get<GeoTransformStatusResponse>(
    `/MapOverLay/${attachmentId}/geo-transform${refresh ? '?refresh=true' : ''}`,
  )
  if (!data) throw new Error('Could not reach the geo-transform service')
  return data
}
